/**
 * `coercion-table` (`LN-WGT03`, the shape ruled in the `LN-JS-M2` review): a
 * pair of operands and the conversions the engine makes before the operator
 * works, one step at a time, the operand row changing type as it goes.
 *
 * <div class="pc-widget" data-widget="coercion-table"
 *      data-pairs="[{&quot;left&quot;:&quot;\&quot;5\&quot;&quot;,&quot;op&quot;:&quot;-&quot;,&quot;right&quot;:&quot;2&quot;,
 *                   &quot;steps&quot;:[&quot;- only works on numbers.&quot;,{&quot;say&quot;:&quot;\&quot;5\&quot; becomes 5.&quot;,&quot;now&quot;:&quot;5 - 2&quot;}],
 *                   &quot;result&quot;:&quot;3&quot;,&quot;type&quot;:&quot;number&quot;}]"
 *      data-caption="Watch which operand changes type.">
 *   <p class="pc-widget-fallback">"5" - 2 turns "5" into 5 and gives 3.</p>
 * </div>
 *
 * `data-pairs` (required): a JSON list of `{ left, op, right, steps, result,
 * type }`, `left`, `right` and `result` JavaScript source text and `type` the
 * result's `typeof`; a step is a string (one sentence, the pair unchanged) or
 * `{ say, now }`, `now` the whole expression after the step, drawn in the
 * operand row. `data-mode` (optional): `"spec"` labels the steps as the
 * specification's; nothing else. `data-caption` (optional). One pair at a
 * time, chosen from a row of pair buttons; step 0 is the pair as written, and
 * the first frame is the first pair with every step listed.
 */

import {
  attachStepper,
  button,
  buildShell,
  el,
  isNonEmptyString,
  isRecord,
  liveNote,
  parseJson,
  type WidgetText,
} from "./dom";

type CoercionStep = string | { say: string; now: string };

interface Pair {
  left: string;
  op: string;
  right: string;
  steps: CoercionStep[];
  result: string;
  type: string;
}

const isStep = (v: unknown): v is CoercionStep =>
  isNonEmptyString(v) || (isRecord(v) && isNonEmptyString(v.say) && isNonEmptyString(v.now));

const isPairs = (value: unknown): value is Pair[] =>
  Array.isArray(value) &&
  value.length > 0 &&
  value.every(
    (pair) =>
      isRecord(pair) &&
      isNonEmptyString(pair.left) &&
      isNonEmptyString(pair.op) &&
      isNonEmptyString(pair.right) &&
      Array.isArray(pair.steps) &&
      pair.steps.length > 0 &&
      pair.steps.every(isStep) &&
      typeof pair.result === "string" &&
      isNonEmptyString(pair.type),
  );

const written = (pair: Pair) => `${pair.left} ${pair.op} ${pair.right}`;
const sayOf = (step: CoercionStep) => (typeof step === "string" ? step : step.say);

/** The expression after `upTo` steps: the last `now` at or before it. */
function expressionAt(pair: Pair, upTo: number): string {
  for (let i = upTo - 1; i >= 0; i--) {
    const step = pair.steps[i];
    if (typeof step !== "string") return step.now;
  }
  return written(pair);
}

export function renderCoercionTable(host: HTMLElement, text: WidgetText): boolean {
  const pairs = parseJson(host.dataset.pairs, isPairs);
  if (!pairs) return false;
  const spec = host.dataset.mode === "spec";

  const shell = buildShell(host, {
    label: text(spec ? "labelCoercionSpec" : "labelCoercion"),
    caption: host.dataset.caption,
  });

  const picker = el("div", "pc-coerce-pairs");
  const operands = el("p", "pc-coerce-operands");
  const list = el("ol", "pc-coerce-steps");
  const result = el("p", "pc-coerce-result");
  const note = liveNote();
  shell.body.append(picker, operands, list, result, note);

  let pair = pairs[0];
  let items: HTMLLIElement[] = [];

  const drawPair = () => {
    list.textContent = "";
    items = pair.steps.map((step) => {
      const item = el("li", undefined, sayOf(step));
      list.append(item);
      return item;
    });
  };

  const draw = (position: number) => {
    operands.textContent = "";
    operands.append(el("code", undefined, expressionAt(pair, position)));
    items.forEach((item, i) => {
      item.classList.toggle("is-active", i === position - 1);
      item.classList.toggle("is-done", i < position - 1);
    });
    const done = position === pair.steps.length;
    result.textContent = "";
    if (done) {
      result.append(el("code", undefined, `${written(pair)}  →  ${pair.result}`));
      result.append(el("span", "pc-coerce-type", `typeof: ${pair.type}`));
    }
    result.hidden = !done;
    note.textContent = position === 0 ? text("coercionWritten", { expr: written(pair) }) : sayOf(pair.steps[position - 1]);
  };

  drawPair();
  const stepper = attachStepper(shell, pair.steps.length + 1, draw, text, {
    liveReadout: false,
    stepLabel: (position) =>
      position === 0 ? text("coercionAsWritten") : text("step", { n: position, total: pair.steps.length }),
  });

  if (pairs.length > 1) {
    const chips = pairs.map((candidate, index) => {
      const chip = button(written(candidate), text("coercionShow", { expr: written(candidate) }));
      chip.classList.add("pc-coerce-pair");
      chip.setAttribute("aria-pressed", String(index === 0));
      chip.addEventListener("click", () => {
        pair = candidate;
        chips.forEach((other, k) => other.setAttribute("aria-pressed", String(k === index)));
        drawPair();
        stepper.setSteps(pair.steps.length + 1);
      });
      picker.append(chip);
      return chip;
    });
  } else {
    picker.hidden = true;
  }
  return true;
}
